import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const EULA_VERSION = '2026-02-25';

export function EulaAcceptanceModal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    const checkEula = async () => {
      const { data } = await supabase.auth.getUser();
      const eulaAccepted = data?.user?.user_metadata?.eula_accepted_version;
      if (!eulaAccepted || eulaAccepted !== EULA_VERSION) {
        setOpen(true);
      }
    };
    checkEula();
  }, [user]);

  const handleAccept = async () => {
    if (!accepted || !user) return;
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          eula_accepted_version: EULA_VERSION,
          eula_accepted_at: new Date().toISOString(),
        }
      });
      
      if (error) throw error;
      setOpen(false);
    } catch (err) {
      console.error('Failed to record EULA acceptance:', err);
      toast.error(t('eula.acceptError', 'Failed to record acceptance. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('eula.modal.title', 'End User License Agreement')}</DialogTitle>
          <DialogDescription>
            {t('eula.modal.description', 'Please review and accept our End User License Agreement to continue using Subtrackr.')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-60 rounded-md border">
          <div className="text-sm text-muted-foreground space-y-3 p-4">
            <p>{t('eula.modal.summary1', 'By accepting this EULA, you agree to the terms governing your use of the Subtrackr platform, including license restrictions, data ownership, payment obligations, and liability limitations.')}</p>
            <p>{t('eula.modal.summary2', 'You retain ownership of all data you upload to the platform. Subtrackr retains all intellectual property rights to the platform itself.')}</p>
            <p>{t('eula.modal.summary3', 'This agreement is governed by Norwegian law. The full agreement is available for your review.')}</p>
          </div>
        </ScrollArea>

        <div className="flex items-start gap-3 mt-2">
          <Checkbox
            id="eula-accept"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked === true)}
          />
          <label htmlFor="eula-accept" className="text-sm leading-tight cursor-pointer">
            {t('eula.modal.checkboxLabel', 'I have read and agree to the')}{' '}
            <Link to="/terms-of-service" target="_blank" className="text-primary underline hover:no-underline">
              {t('eula.modal.eulaLink', 'End User License Agreement')}
            </Link>
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!accepted || isSubmitting}
            className="w-full"
          >
            {isSubmitting
              ? t('eula.modal.accepting', 'Accepting...')
              : t('eula.modal.accept', 'Accept & Continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
